import React, { useState } from 'react';
import {
  TrendingUp, BarChart3, PieChart, Activity, Target, Clock,
  Award, BookOpen, Users, Calendar, ChevronRight, Download,
  Filter, RefreshCw, Info, Zap, Brain, Trophy, Star,
  ArrowUp, ArrowDown, Minus, CheckCircle, XCircle, AlertCircle,
  Timer, Percent, Hash, Layers, Grid3x3, List, Eye
} from 'lucide-react';

interface SkillProgress {
  id: string;
  name: string;
  category: string;
  level: number; // 1-5
  progress: number; // 0-100
  coursesCompleted: number;
  totalCourses: number;
  hoursSpent: number;
  lastActivity: string;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
}

interface LearningMetric {
  label: string;
  value: string | number;
  change: number;
  trend: 'up' | 'down' | 'stable';
  icon: React.ReactNode;
  color: string;
}

interface CourseProgress {
  id: string;
  name: string;
  category: string;
  progress: number;
  timeSpent: number;
  estimatedTimeLeft: number;
  lastAccessed: string;
  deadline?: string;
  score?: number;
}

export default function LearnerProgress() {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const overallStats = {
    totalHours: 156,
    coursesCompleted: 12,
    coursesInProgress: 5,
    averageScore: 85,
    learningStreak: 15,
    certificatesEarned: 8,
    skillsAcquired: 24,
    completionRate: 78
  };

  const learningMetrics: LearningMetric[] = [
    {
      label: 'Total Learning Time',
      value: '156h 30m',
      change: 12,
      trend: 'up',
      icon: <Clock className="w-5 h-5" />,
      color: 'from-blue-500 to-indigo-600'
    },
    {
      label: 'Completion Rate',
      value: '78%',
      change: 5,
      trend: 'up',
      icon: <CheckCircle className="w-5 h-5" />,
      color: 'from-green-500 to-emerald-600'
    },
    {
      label: 'Average Score',
      value: '85%',
      change: -2,
      trend: 'down',
      icon: <Trophy className="w-5 h-5" />,
      color: 'from-purple-500 to-pink-600'
    },
    {
      label: 'Learning Velocity',
      value: '2.3x',
      change: 0,
      trend: 'stable',
      icon: <Zap className="w-5 h-5" />,
      color: 'from-orange-500 to-red-600'
    }
  ];

  const skillsProgress: SkillProgress[] = [
    {
      id: '1',
      name: 'React Development',
      category: 'Frontend',
      level: 4,
      progress: 85,
      coursesCompleted: 4,
      totalCourses: 5,
      hoursSpent: 48,
      lastActivity: '2 days ago',
      trend: 'up',
      trendValue: 12
    },
    {
      id: '2',
      name: 'Data Analysis',
      category: 'Data Science',
      level: 3,
      progress: 65,
      coursesCompleted: 2,
      totalCourses: 4,
      hoursSpent: 32,
      lastActivity: '1 week ago',
      trend: 'up',
      trendValue: 8
    },
    {
      id: '3',
      name: 'Leadership',
      category: 'Soft Skills',
      level: 3,
      progress: 70,
      coursesCompleted: 3,
      totalCourses: 4,
      hoursSpent: 24,
      lastActivity: '3 days ago',
      trend: 'stable',
      trendValue: 0
    },
    {
      id: '4',
      name: 'Cloud Architecture',
      category: 'Cloud',
      level: 2,
      progress: 40,
      coursesCompleted: 1,
      totalCourses: 3,
      hoursSpent: 16,
      lastActivity: '5 days ago',
      trend: 'down',
      trendValue: -5
    }
  ];

  const coursesInProgress: CourseProgress[] = [
    {
      id: '1',
      name: 'Advanced React Patterns',
      category: 'Development',
      progress: 75,
      timeSpent: 18,
      estimatedTimeLeft: 6,
      lastAccessed: '2 hours ago',
      deadline: '2024-02-15',
      score: 88
    },
    {
      id: '2',
      name: 'Machine Learning Basics',
      category: 'Data Science',
      progress: 45,
      timeSpent: 12,
      estimatedTimeLeft: 15,
      lastAccessed: 'Yesterday',
      score: 82
    },
    {
      id: '3',
      name: 'Public Speaking Mastery',
      category: 'Soft Skills',
      progress: 60,
      timeSpent: 8,
      estimatedTimeLeft: 5,
      lastAccessed: '3 days ago',
      score: 90
    }
  ];

  // Mock data for the learning activity chart
  const weeklyActivity = [
    { day: 'Mon', hours: 2.5 },
    { day: 'Tue', hours: 3.2 },
    { day: 'Wed', hours: 1.8 },
    { day: 'Thu', hours: 4.1 },
    { day: 'Fri', hours: 2.7 },
    { day: 'Sat', hours: 3.5 },
    { day: 'Sun', hours: 1.2 }
  ];

  const getLevelStars = (level: number) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < level ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const getTrendIcon = (trend: string, value: number) => {
    if (trend === 'up') return <ArrowUp className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <ArrowDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <TrendingUp className="w-7 h-7" />
              Learning Progress & Analytics
            </h1>
            <p className="text-indigo-100">
              Track your learning journey, skills development, and performance metrics
            </p>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>
            <button className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
              <Download className="w-5 h-5" />
              <span className="hidden sm:inline">Export Report</span>
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {learningMetrics.map((metric, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className={`p-2 bg-gradient-to-r ${metric.color} rounded-lg`}>
                  {metric.icon}
                </div>
                <div className="flex items-center gap-1">
                  {getTrendIcon(metric.trend, metric.change)}
                  <span className="text-xs">
                    {metric.change > 0 ? '+' : ''}{metric.change}%
                  </span>
                </div>
              </div>
              <p className="text-2xl font-bold">{metric.value}</p>
              <p className="text-xs text-indigo-100">{metric.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Learning Activity Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              Weekly Learning Activity
            </h2>
            <button className="p-2 text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Bar Chart */}
          <div className="h-64 flex items-end justify-between gap-4">
            {weeklyActivity.map((day, index) => (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div className="w-full flex flex-col items-center">
                  <span className="text-xs text-gray-600 mb-2">
                    {day.hours}h
                  </span>
                  <div
                    className="w-full bg-gradient-to-t from-indigo-600 to-purple-500 rounded-t-lg hover:from-indigo-700 hover:to-purple-600 transition-colors cursor-pointer"
                    style={{ height: `${(day.hours / 5) * 100}%` }}
                  />
                </div>
                <span className="text-sm text-gray-600 mt-2">{day.day}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">19h</p>
              <p className="text-xs text-gray-500">Total This Week</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">2.7h</p>
              <p className="text-xs text-gray-500">Daily Average</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">+15%</p>
              <p className="text-xs text-gray-500">vs Last Week</p>
            </div>
          </div>
        </div>

        {/* Goals & Targets */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-600" />
            Monthly Goals
          </h2>

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Complete 5 Courses</span>
                <span className="text-sm font-medium text-gray-900">3/5</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '60%' }} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Learn 40 Hours</span>
                <span className="text-sm font-medium text-gray-900">28/40h</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full" style={{ width: '70%' }} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Earn 3 Certificates</span>
                <span className="text-sm font-medium text-gray-900">2/3</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-purple-500 h-2 rounded-full" style={{ width: '66%' }} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Maintain Streak</span>
                <span className="text-sm font-medium text-gray-900">15 days</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-orange-500 h-2 rounded-full" style={{ width: '100%' }} />
              </div>
            </div>
          </div>

          <button className="w-full mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
            Set New Goals
          </button>
        </div>
      </div>

      {/* Skills Progress */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Brain className="w-5 h-5 text-indigo-600" />
              Skills Development
            </h2>
            <div className="flex items-center gap-2">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              >
                <option value="all">All Categories</option>
                <option value="technical">Technical</option>
                <option value="soft">Soft Skills</option>
                <option value="business">Business</option>
              </select>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-lg ${
                    viewMode === 'grid' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-lg ${
                    viewMode === 'list' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {skillsProgress.map((skill) => (
                <div key={skill.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900">{skill.name}</h3>
                      <p className="text-xs text-gray-500">{skill.category}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {getTrendIcon(skill.trend, skill.trendValue)}
                      <span className="text-xs text-gray-600">
                        {skill.trendValue > 0 ? '+' : ''}{skill.trendValue}%
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 mb-3">
                    {getLevelStars(skill.level)}
                    <span className="text-xs text-gray-500 ml-2">Level {skill.level}</span>
                  </div>

                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500">Progress</span>
                      <span className="text-xs font-medium text-gray-900">{skill.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full"
                        style={{ width: `${skill.progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-gray-500">Courses</span>
                      <p className="font-medium text-gray-900">
                        {skill.coursesCompleted}/{skill.totalCourses}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Time Invested</span>
                      <p className="font-medium text-gray-900">{skill.hoursSpent}h</p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mt-3">
                    Last activity: {skill.lastActivity}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {skillsProgress.map((skill) => (
                <div key={skill.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div>
                      <h3 className="font-medium text-gray-900">{skill.name}</h3>
                      <p className="text-sm text-gray-500">{skill.category}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-1">
                      {getLevelStars(skill.level)}
                    </div>

                    <div className="w-32">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">Progress</span>
                        <span className="text-xs font-medium">{skill.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-indigo-600 h-1.5 rounded-full"
                          style={{ width: `${skill.progress}%` }}
                        />
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {skill.coursesCompleted}/{skill.totalCourses} courses
                      </p>
                      <p className="text-xs text-gray-500">{skill.hoursSpent}h invested</p>
                    </div>

                    <div className="flex items-center gap-1">
                      {getTrendIcon(skill.trend, skill.trendValue)}
                      <span className="text-sm">
                        {skill.trendValue > 0 ? '+' : ''}{skill.trendValue}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Courses in Progress */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            Active Courses Performance
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time Spent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Est. Time Left</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {coursesInProgress.map((course) => (
                <tr key={course.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{course.name}</p>
                      <p className="text-xs text-gray-500">{course.category}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              course.progress >= 75 ? 'bg-green-500' :
                              course.progress >= 50 ? 'bg-blue-500' :
                              course.progress >= 25 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${course.progress}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{course.progress}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{course.timeSpent}h</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{course.estimatedTimeLeft}h</td>
                  <td className="px-6 py-4">
                    {course.score && (
                      <span className={`text-sm font-medium ${
                        course.score >= 80 ? 'text-green-600' :
                        course.score >= 60 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {course.score}%
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {course.deadline && new Date(course.deadline).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      course.progress >= 75 ? 'bg-green-100 text-green-800' :
                      course.progress >= 50 ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {course.progress >= 75 ? 'Nearly Complete' :
                       course.progress >= 50 ? 'On Track' :
                       'Needs Attention'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}